import {
    AfterViewInit,
    Component,
    ContentChildren,
    ElementRef,
    Host,
    Inject,
    input,
    output,
    QueryList,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DsListItemDescriptionDirective, DsListItemDirective, DsListItemTitleDirective } from './ds-list-item.directives';
import { FocusableOption } from '@angular/cdk/a11y';
import { DS_LIST_COMPONENT, IDsListComponent } from '../ds-list.interface';

/**
 * A list item component that can be used for both static list items and selectable list options.
 *
 * Supports:
 * - Single and multiple selection modes
 * - Router links and external links
 * - Button actions
 * - Keyboard navigation
 * - Disabled state
 * - Accessibility features (ARIA, focus management)
 *
 * Can be used with two selectors:
 * - `<ds-list-item>` - For static, non-selectable list items
 * - `<ds-list-option>` - For selectable list options
 *
 * Note: Both selectors share the same implementation to reduce code duplication
 * and maintain consistent behavior across static and selectable list items.
 */
@Component({
    selector: 'ds-list-item,ds-list-option',
    hostDirectives: [DsListItemDirective],
    imports: [CommonModule, RouterModule],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ds-list-item.component.html',
    styleUrl: './ds-list-item.component.scss',
    host: {
        '[class.DsListOption]': 'isListOption',
        '[class.DsListItem]': 'isListItem',
        '[class.DsListOption--selected]': 'isSelected()',
        '[attr.disabled]': 'disabled',
        '[attr.tabindex]': 'getTabIndex()',
        '[attr.size]': 'sizeClass',
        role: 'listitem',
        '(click)': 'onItemClick()',
        '(keydown)': 'onKeyDown($event)',
    }
})
export class DsListItemComponent implements AfterViewInit, FocusableOption {
    /** The unique value identifier for this list item */
    value = input<string>();

    /** Whether this item is currently selected */
    selected = input<boolean>();

    /**
     * Whether this item is disabled and cannot be interacted with.
     *
     * Can be used as:
     * - `[disabled]="true"` - Property binding (aliased for better DX)
     * - `[disabledInput]="true"` - Direct property binding
     * - `ds-list-item-disabled` - Directive attribute
     *
     * Note: Uses alias to provide better developer experience while avoiding
     * conflict with the FocusableOption interface's disabled property.
     * The alias is intentional for better API ergonomics.
     */
    readonly disabledInput = input<boolean, boolean | string>(false, {
        // eslint-disable-next-line @angular-eslint/no-input-rename -- Intentional alias for better DX
        alias: 'disabled',
        transform: (value: boolean | string) => {
            // Handle both boolean and string 'true'/'false' values
            return value === true || value === 'true';
        },
    });

    /** Angular router link for navigation */
    routerLink = input<string | string[]>();

    /** External URL for navigation */
    href = input<string>();

    /** Target attribute for link navigation */
    target = input<'_blank' | '_self' | '_parent' | '_top'>();

    /** Optional callback function executed when the item is clicked */
    buttonAction = input<() => void>();

    /** Emits the item's value when selection state changes */
    selectedChange = output<string>();

    /** Emits when the button action is executed */
    action = output<void>();

    /** Size class applied to the list item, inherited from parent list */
    sizeClass: string;

    /**
     * Gets the disabled state for FocusableOption interface compatibility.
     * Combines the disabled input and the disabled directive.
     */
    get disabled(): boolean {
        return this.disabledInput() || this.hasDisabledDirective;
    }

    /**
     * Gets the disabled state, combining both the disabled input and the disabled directive.
     * @deprecated Use the disabled getter instead
     */
    get isDisabled() {
        return this.disabled;
    }

    /**
     * Checks if this is a list option element (ds-list-option tag).
     */
    get isListOption(): boolean {
        return this.elRef.nativeElement.tagName.toLowerCase() === 'ds-list-option';
    }

    /**
     * Checks if this is a list item element (ds-list-item tag).
     */
    get isListItem(): boolean {
        return this.elRef.nativeElement.tagName.toLowerCase() === 'ds-list-item';
    }

    /**
     * Determines the tabindex value for keyboard navigation.
     *
     * Returns:
     * - -1 if disabled (never tabbable)
     * - -1 if in tabbable mode and has a link (the link itself is tabbable)
     * - 0 if in tabbable mode without a link (container is tabbable)
     * - -1 in arrow key navigation mode (default)
     */
    getTabIndex(): number {
        // Disabled items should never be tabbable
        if (this.disabled) {
            return -1;
        }

        // If tabbable mode is enabled
        if (this.parent.tabbable()) {
            // If there's an anchor link, it should be tabbable, not the container
            if (this.routerLink() || this.href()) {
                return -1; // Container not tabbable, anchor will be
            }
            // No anchor, so the container should be tabbable
            return 0;
        }

        // Default: not tabbable (arrow key navigation mode)
        return -1;
    }

    /** Content children with title directive */
    @ContentChildren(DsListItemTitleDirective, { descendants: true }) titles!: QueryList<DsListItemTitleDirective>;

    /** Content children with description directive */
    @ContentChildren(DsListItemDescriptionDirective, { descendants: true })
    descriptions!: QueryList<DsListItemDescriptionDirective>;

    /** Signal indicating if the item has title or description content */
    hasTitleOrDescription = signal<boolean>(false);

    /** Flag indicating if a disabled directive is present on the element */
    hasDisabledDirective = false;

    /** Delay in milliseconds before closing parent overlay after item interaction */
    readonly #OVERLAY_CLOSE_DELAY_MS = 50;

    constructor(
        @Host() @Inject(DS_LIST_COMPONENT) public parent: IDsListComponent,
        public elRef: ElementRef
    ) {
        this.sizeClass = this.parent.size() ?? 'md';

        this.#checkDisabledItems();
    }

    /**
     * Lifecycle hook called after the component's view has been fully initialized.
     *
     * Updates the hasTitleOrDescription signal based on projected content.
     */
    ngAfterViewInit(): void {
        this.hasTitleOrDescription.set(!!this.titles.length || !!this.descriptions.length);
    }

    /**
     * Handles click interaction on the list item.
     * Uses Angular's event binding for automatic cleanup and better performance.
     *
     * Performs the following actions if not disabled and has a value:
     * - Executes the button action callback if provided
     * - Emits the action event
     * - Emits the selection change event with the item's value
     * - Closes parent overlay/context menu if not navigating to a link
     */
    onItemClick(): void {
        // Only process click if item has a value and is not disabled
        const itemValue = this.value();
        if (!this.disabled && itemValue) {
            // Execute button action if provided
            const action = this.buttonAction();
            if (action) {
                action();
                this.action.emit();
            }
            this.selectedChange.emit(itemValue);
        }
    }

    /**
     * Handles keyboard interaction on the list item.
     * Triggers the item action on Enter or Space key press.
     *
     * @param event The keyboard event
     */
    onKeyDown(event: KeyboardEvent): void {
        // Only handle Enter and Space keys
        if (event.key === 'Enter' || event.key === ' ') {
            // Prevent default behavior (e.g., page scroll on Space)
            event.preventDefault();

            // For links (router links or external links), let the browser handle the navigation
            if (this.routerLink() || this.href()) {
                // Trigger a click on the link element
                const linkElement = this.elRef.nativeElement.querySelector('a');
                if (linkElement) {
                    linkElement.click();
                }
            } else {
                // For button actions or non-actionable items, trigger the click handler
                this.onItemClick();
            }
        }
    }

    /**
     * Determines if this list item is currently selected.
     *
     * An item is considered selected if:
     * - The `selected` input is true, OR
     * - The parent's selectedItems array includes this item's value
     *
     * @returns True if the item is selected, false otherwise
     */
    isSelected(): boolean {
        const selectedItems = this.parent.selectedItems();
        const itemValue = this.value();
        return (
            this.selected() === true ||
            (itemValue !== undefined && selectedItems !== null && selectedItems?.includes(itemValue))
        );
    }

    /**
     * Focuses the list item element.
     * Part of the FocusableOption interface for CDK focus management.
     */
    focus(): void {
        this.elRef.nativeElement.focus();
    }

    /**
     * Gets the label for the list item.
     * Part of the FocusableOption interface for CDK focus management.
     *
     * @returns The item's value as its label, or empty string if no value
     */
    getLabel?(): string {
        return this.value() ?? '';
    }

    /**
     * Checks if the host element has a disabled directive attribute.
     * Sets the hasDisabledDirective flag accordingly.
     */
    #checkDisabledItems(): void {
        const hostElement = this.elRef.nativeElement;
        this.hasDisabledDirective =
            hostElement.hasAttribute('ds-list-item-disabled') || hostElement.hasAttribute('ds-list-option-disabled');
    }
}

/**
 * A header component for grouping related items in a list.
 * Typically used to display category labels or section titles within a list.
 *
 * Styled with emphasis text and secondary color for visual hierarchy.
 */
@Component({
    selector: 'ds-list-header',
    imports: [CommonModule],
    template: `<ng-content></ng-content>`,
    host: {
        class: 'DsListHeader u-text-emphasis u-color-text-secondary',
    },
    styleUrl: './ds-list-item.component.scss'
})
export class DsListHeaderComponent {}
